/*
 * Purpose: Execution monitor — live runs from runtime + logStore
 * Last modified: 2026-06-28 | Agent
 * Completeness: 90
 */
import { useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader, CheckCircle, AlertCircle, Clock, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { useLogStore } from '@/stores/logStore';
import { useExecutionRuntimeStore } from '@/stores/executionRuntimeStore';
import { useWorkflowStore } from '@/stores/workflowStore';
import { buildMonitorFromRuntime } from '@/lib/executionInsights';
import { missionMapHref } from '@/lib/appRoutes';
import { toast } from 'sonner';
import { analyzeInlineValidation, autoFixWorkflowInline, autoFixWorkflowInlineSafe } from '@/services/inlineValidation';
import { simulateFlowTrace } from '@/services/flowTraceSimulation';
import { evaluateFlowTracingTruth } from '@/services/flowTracingTruth';
import { useExecutionTraceStore } from '@/stores/executionTraceStore';
import { getSmartBlockInputRequirements } from '@/services/blockAutoConfigure';
import { useActivityLog } from '@/components/ActivityLog';

export function ExecutionMonitor() {
  const running = useExecutionRuntimeStore((s) => s.running);
  const logs = useLogStore((s) => s.logs);
  const workflows = useWorkflowStore((s) => s.workflows);
  const currentWorkflow = useWorkflowStore((s) => s.currentWorkflow);
  const selectNode = useWorkflowStore((s) => s.selectNode);
  const autoConfigureNode = useWorkflowStore((s) => s.autoConfigureNode);
  const applyAssembledWorkflowIfCurrent = useWorkflowStore((s) => s.applyAssembledWorkflowIfCurrent);
  const saveWorkflow = useWorkflowStore((s) => s.saveWorkflow);
  const stopRun = useExecutionRuntimeStore((s) => s.stopRun);
  const trace = useExecutionTraceStore((s) =>
    currentWorkflow?.id ? s.traces[currentWorkflow.id] : undefined
  );
  const { addActivity } = useActivityLog();

  const selectedExecution = useMemo(() => {
    const wf = workflows.find((w) => running.some((r) => r.workflowId === w.id));
    return buildMonitorFromRuntime(running, logs, wf ?? null);
  }, [running, logs, workflows]);
  const inline = useMemo(
    () => (currentWorkflow ? analyzeInlineValidation(currentWorkflow) : null),
    [currentWorkflow]
  );
  const simulation = useMemo(
    () => (currentWorkflow ? simulateFlowTrace(currentWorkflow) : null),
    [currentWorkflow]
  );
  const truth = useMemo(
    () => (currentWorkflow ? evaluateFlowTracingTruth(currentWorkflow, trace, inline, simulation) : null),
    [currentWorkflow, trace, inline, simulation]
  );
  const humanInputCount = useMemo(
    () => (currentWorkflow ? currentWorkflow.nodes.filter((n) => getSmartBlockInputRequirements(n).length > 0).length : 0),
    [currentWorkflow]
  );

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'running':
        return <Loader className="w-5 h-5 text-blue-600 animate-spin" />;
      case 'failed':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-muted-foreground" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500/10 text-green-700 border-green-500/20';
      case 'running':
        return 'bg-blue-500/10 text-blue-700 border-blue-500/20';
      case 'failed':
        return 'bg-red-500/10 text-red-700 border-red-500/20';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  if (running.length === 0) {
    return (
      <Card className="p-6 text-center text-muted-foreground space-y-3">
        <p className="text-sm">No active execution — start a flow from the Mission map.</p>
        <Button asChild size="sm" variant="outline">
          <Link to={missionMapHref()}>Open Mission map</Link>
        </Button>
      </Card>
    );
  }

  if (!selectedExecution) {
    return (
      <Card className="p-6 text-center text-muted-foreground">
        <p className="text-sm">Loading execution data…</p>
      </Card>
    );
  }

  const handleStop = () => {
    if (stopRun(selectedExecution.workflowId)) {
      toast.message(`${selectedExecution.workflowName} stopped`);
    }
  };

  const totalDuration = selectedExecution.steps.reduce((sum, step) => sum + (step.duration || 0), 0);
  const handleSafeFixAll = () => {
    if (!currentWorkflow) return;
    const safeOnly = currentWorkflow.missionMeta?.smartAutoFixPolicy === 'safe-only';
    const result = safeOnly ? autoFixWorkflowInlineSafe(currentWorkflow) : autoFixWorkflowInline(currentWorkflow);
    if (result.changes.length === 0) {
      toast.message('No safe Smart Blocks fixes needed');
      return;
    }
    const applied = applyAssembledWorkflowIfCurrent(currentWorkflow.id, result.workflow);
    if (!applied) {
      toast.warning('Mission changed — preflight fix not applied');
      return;
    }
    saveWorkflow();
    addActivity('Preflight Smart Blocks fix', `${result.changes.length} change(s)`, 'update');
    toast.success(`Preflight fixed ${result.safetySummary.safeAutoFix} safe issue(s)`);
  };

  return (
    <div className="space-y-6">
      {currentWorkflow && truth && simulation && (
        <Card className="p-4 space-y-3 border-primary/20 bg-primary/5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-foreground">Preflight gate</p>
              <p className="text-xs text-muted-foreground">
                Truth {Math.round(truth.confidence * 100)}% · {truth.label}
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={handleSafeFixAll}>
              Fix all safe items
            </Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            <div className="rounded border border-border p-2">Simulation: {simulation.predictedComplete ? 'Complete' : 'Incomplete'}</div>
            <div className="rounded border border-border p-2">Failpoints: {simulation.failpointNodeIds.length}</div>
            <div className="rounded border border-border p-2">Human input: {humanInputCount}</div>
            <div className="rounded border border-border p-2">Validation errors: {inline?.suggestions.filter((s) => s.type === 'error').length ?? 0}</div>
          </div>
          {trace?.failedNodeId && (
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => selectNode(trace.failedNodeId!)}>
                Jump to failpoint
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  const changes = autoConfigureNode(trace.failedNodeId!);
                  toast.message(
                    changes.length ? 'Applied Smart Blocks on failed node' : 'Failed node already configured'
                  );
                }}
              >
                Smart Blocks failed node
              </Button>
            </div>
          )}
        </Card>
      )}
      <Card className="p-6 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <h3 className="text-xl font-semibold text-foreground">{selectedExecution.workflowName}</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Started {selectedExecution.startTime.toLocaleTimeString()}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={getStatusColor(selectedExecution.status)}>
              {selectedExecution.status.charAt(0).toUpperCase() + selectedExecution.status.slice(1)}
            </Badge>
            <Button variant="outline" size="sm" onClick={handleStop}>
              <Square className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Progress</span>
            <span className="font-semibold text-foreground">{selectedExecution.progress}%</span>
          </div>
          <Progress value={selectedExecution.progress} className="h-2" />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="p-3 rounded border border-border">
            <p className="text-xs text-muted-foreground">Log steps</p>
            <p className="text-2xl font-bold text-foreground">{selectedExecution.steps.length}</p>
          </div>
          <div className="p-3 rounded border border-border">
            <p className="text-xs text-muted-foreground">Completed</p>
            <p className="text-2xl font-bold text-green-600">
              {selectedExecution.steps.filter((s) => s.status === 'completed').length}
            </p>
          </div>
          <div className="p-3 rounded border border-border">
            <p className="text-xs text-muted-foreground">Live runs</p>
            <p className="text-2xl font-bold text-foreground">{running.length}</p>
          </div>
          <div className="p-3 rounded border border-border">
            <p className="text-xs text-muted-foreground">Failed steps</p>
            <p className="text-2xl font-bold text-red-600">
              {selectedExecution.steps.filter((s) => s.status === 'failed').length}
            </p>
          </div>
        </div>
      </Card>

      <Card className="p-4">
        <h4 className="font-semibold text-foreground mb-4">Execution log (live)</h4>
        <ScrollArea className="h-96">
          <div className="space-y-3 pr-4">
            {selectedExecution.steps.map((step, index) => (
              <div key={step.id} className="p-4 rounded border border-border hover:bg-muted/50 transition-colors">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 mt-1">{getStatusIcon(step.status)}</div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground">
                      {index + 1}. {step.nodeName}
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-1">Live path trace node</p>
                    {step.error && (
                      <div className="p-2 rounded bg-red-500/10 border border-red-500/20 mt-2">
                        <p className="text-xs text-red-700 font-mono">{step.error}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </Card>

      {totalDuration > 0 && (
        <p className="text-xs text-muted-foreground text-center">
          Data sourced from execution logs — open Mission map for full control.
        </p>
      )}
    </div>
  );
}
